import { c as _c } from "react/compiler-runtime";
import { IconAlertCircle, IconRosetteDiscountCheck, IconSchool, IconSpy } from "@tabler/icons-react";
import { KeyValue } from "@/components/domain/key-value";
import { KeyValueGrid } from "@/components/domain/key-value-grid";
import { RawDataDialog } from "@/components/domain/registration/raw-data-dialog";
import { RelativeAgeString } from "@/components/domain/relative-age";
import { RelativeExpiryString } from "@/components/domain/relative-expiry";
import { ReportSection } from "@/components/domain/report-section";
import { ProviderLogo } from "@/components/icons/provider-logo";
import { sections } from "@/lib/constants/sections";
import type { RegistrationResponse } from "@domainstack/types";
import { ResponsiveTooltip, ResponsiveTooltipContent, ResponsiveTooltipTrigger } from "@domainstack/ui/responsive-tooltip";
import { formatDate, formatDateTimeUtc, toDateTimeAttr } from "@domainstack/utils/date";
type RegistrantView = {
  organization: string;
  country: string;
  state?: string;
};
function getUnavailableMessage(data: RegistrationResponse): string {
  if (data.unavailableReason === "timeout") {
    return "WHOIS/RDAP lookup timed out. This may be a temporary issue with the registry's servers.";
  }
  if (data.unavailableReason === "unsupported_tld") {
    return `The .${data.tld} registry does not publish public WHOIS/RDAP data. Registration details cannot be verified for this domain.`;
  }
  return "Registration information could not be retrieved at this time.";
}
function getRegistrationSource(data: RegistrationResponse) {
  const serverUrl = data.rdapServers && data.rdapServers.length > 0 ? data.rdapServers[data.rdapServers.length - 1] : undefined;
  const serverName = serverUrl ? extractSourceDomain(serverUrl) ?? "RDAP" : data.whoisServer ?? "WHOIS";
  const learnUrl = data.source === "rdap" ? "https://about.rdap.org/" : "https://en.wikipedia.org/wiki/WHOIS";
  return {
    serverUrl,
    serverName,
    learnUrl
  };
}
function RegistrationUnavailableNotice(t0) {
  const $ = _c(6);
  const {
    data
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconAlertCircle className="mt-0.5 size-4 shrink-0 text-yellow-800 dark:text-yellow-200" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="font-medium text-yellow-800 dark:text-yellow-200">Registration Data Unavailable</p>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== data) {
    t3 = getUnavailableMessage(data);
    $[2] = data;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="flex items-start gap-3 rounded-lg border border-warning-border bg-warning-border/10 p-4 text-sm backdrop-blur-lg dark:bg-warning-border/10">{t1}<div className="space-y-1">{t2}<p className="text-yellow-800/90 dark:text-yellow-200/80">{t3}</p></div></div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function RegistrarVerifiedBy(t0) {
  const $ = _c(13);
  const {
    serverUrl,
    serverName,
    learnUrl,
    source
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ResponsiveTooltipTrigger nativeButton={false} render={<IconRosetteDiscountCheck className="!size-3.5 text-muted-foreground/80" aria-hidden="true" />} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== serverName || $[2] !== serverUrl) {
    t2 = serverUrl ? <a href={serverUrl} target="_blank" rel="noopener" className="underline underline-offset-2">{serverName}</a> : serverName;
    $[1] = serverName;
    $[2] = serverUrl;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span>Verified by{" "}<span className="font-medium">{t2}</span></span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `Learn about ${source === "rdap" ? "RDAP" : "WHOIS"}`;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconSchool className="size-3" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== learnUrl || $[8] !== t4) {
    t6 = <a href={learnUrl} target="_blank" rel="noopener" title={t4} className="text-muted/80">{t5}</a>;
    $[7] = learnUrl;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <ResponsiveTooltip>{t1}<ResponsiveTooltipContent><div className="flex items-center gap-[5px]">{t3}{t6}</div></ResponsiveTooltipContent></ResponsiveTooltip>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
function RegistrationDetailsGrid(t0) {
  const $ = _c(53);
  const {
    data
  } = t0;
  let T0;
  let T1;
  let isHidden;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== data) {
    const registrant = extractRegistrantView(data);
    const {
      serverUrl,
      serverName,
      learnUrl
    } = getRegistrationSource(data);
    isHidden = data.privacyEnabled || !registrant;
    T1 = KeyValueGrid;
    t3 = 2;
    const t5 = data.registrarProvider?.name || "Unknown";
    let t6;
    if ($[8] !== data.registrarProvider) {
      t6 = data.registrarProvider?.id ? <ProviderLogo providerId={data.registrarProvider.id} providerName={data.registrarProvider.name} /> : undefined;
      $[8] = data.registrarProvider;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== data.source || $[11] !== learnUrl || $[12] !== serverName || $[13] !== serverUrl) {
      t7 = <RegistrarVerifiedBy serverUrl={serverUrl} serverName={serverName} learnUrl={learnUrl} source={data.source} />;
      $[10] = data.source;
      $[11] = learnUrl;
      $[12] = serverName;
      $[13] = serverUrl;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
      t4 = <KeyValue label="Registrar" value={t5} leading={t6} suffix={t7} />;
      $[15] = t5;
      $[16] = t6;
      $[17] = t7;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    T0 = KeyValue;
    t1 = "Registrant";
    t2 = registrant && !data.privacyEnabled ? formatRegistrant(registrant) : "Hidden";
    $[0] = data;
    $[1] = T0;
    $[2] = T1;
    $[3] = isHidden;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    T0 = $[1];
    T1 = $[2];
    isHidden = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[19] !== isHidden) {
    t5 = isHidden ? <IconSpy className="text-muted-foreground" aria-hidden="true" /> : undefined;
    $[19] = isHidden;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== T0 || $[22] !== t1 || $[23] !== t2 || $[24] !== t5) {
    t6 = <T0 label={t1} value={t2} leading={t5} />;
    $[21] = T0;
    $[22] = t1;
    $[23] = t2;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== data.creationDate) {
    t7 = data.creationDate ? <time dateTime={toDateTimeAttr(data.creationDate)} suppressHydrationWarning={true}>{formatDate(data.creationDate)}</time> : "Unknown";
    $[26] = data.creationDate;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== data.creationDate) {
    t8 = data.creationDate ? <time dateTime={toDateTimeAttr(data.creationDate)} suppressHydrationWarning={true}>{formatDateTimeUtc(data.creationDate)}</time> : undefined;
    $[28] = data.creationDate;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== data.creationDate) {
    t9 = data.creationDate ? <span className="text-[11px] leading-none text-muted-foreground"><RelativeAgeString from={data.creationDate} /></span> : null;
    $[30] = data.creationDate;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t10 = <KeyValue label="Created" value={t7} valueTooltip={t8} suffix={t9} />;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== data.expirationDate) {
    t11 = data.expirationDate ? <time dateTime={toDateTimeAttr(data.expirationDate)} suppressHydrationWarning={true}>{formatDate(data.expirationDate)}</time> : "Unknown";
    $[36] = data.expirationDate;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== data.expirationDate) {
    t12 = data.expirationDate ? <time dateTime={toDateTimeAttr(data.expirationDate)} suppressHydrationWarning={true}>{formatDateTimeUtc(data.expirationDate)}</time> : undefined;
    $[38] = data.expirationDate;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== data.expirationDate) {
    t13 = data.expirationDate ? <span className="text-[11px] leading-none text-muted-foreground"><RelativeExpiryString to={data.expirationDate} dangerDays={30} warnDays={45} /></span> : null;
    $[40] = data.expirationDate;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t11 || $[43] !== t12 || $[44] !== t13) {
    t14 = <KeyValue label="Expires" value={t11} valueTooltip={t12} suffix={t13} />;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== T1 || $[47] !== t10 || $[48] !== t14 || $[49] !== t3 || $[50] !== t4 || $[51] !== t6) {
    t15 = <T1 colsDesktop={t3}>{t4}{t6}{t10}{t14}</T1>;
    $[46] = T1;
    $[47] = t10;
    $[48] = t14;
    $[49] = t3;
    $[50] = t4;
    $[51] = t6;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
}
export function RegistrationSection(t0) {
  const $ = _c(14);
  const {
    data
  } = t0;
  if (!data) {
    return null;
  }
  const isWhoisUnavailable = data.status === "unknown";
  let t1;
  if ($[0] !== data) {
    t1 = getRegistrationSource(data);
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    serverUrl,
    serverName
  } = t1;
  let t2;
  if ($[2] !== data.domain || $[3] !== data.rawResponse || $[4] !== data.source || $[5] !== serverName || $[6] !== serverUrl) {
    t2 = data.rawResponse ? <RawDataDialog domain={data.domain} format={data.source === "rdap" ? "RDAP" : "WHOIS"} data={data.rawResponse} serverName={serverName} serverUrl={serverUrl} /> : undefined;
    $[2] = data.domain;
    $[3] = data.rawResponse;
    $[4] = data.source;
    $[5] = serverName;
    $[6] = serverUrl;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== data || $[9] !== isWhoisUnavailable) {
    t3 = isWhoisUnavailable ? <RegistrationUnavailableNotice data={data} /> : <RegistrationDetailsGrid data={data} />;
    $[8] = data;
    $[9] = isWhoisUnavailable;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <ReportSection {...sections.registration} headerActions={t2}>{t3}</ReportSection>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function formatRegistrant(reg: {
  organization: string;
  country: string;
  state?: string;
}) {
  const org = (reg.organization || "").trim();
  const country = (reg.country || "").trim();
  const state = (reg.state || "").trim();
  const parts = [] as string[];
  if (org) parts.push(org);
  const loc = [state, country].filter(Boolean).join(", ");
  if (loc) parts.push(loc);
  if (parts.length === 0) return "Unavailable";
  return parts.join(" — ");
}
function extractRegistrantView(record: RegistrationResponse): RegistrantView | null {
  const registrant = record.contacts?.find(c => c.type === "registrant");
  if (!registrant) return null;
  const organization = (registrant.organization || registrant.name || "").trim() || "Unknown";
  const country = registrant.country || registrant.countryCode || "";
  const state = registrant.state || "" || undefined;
  return {
    organization,
    country,
    state
  };
}
function extractSourceDomain(input: string | undefined | null): string | undefined {
  if (!input) return;
  const value = String(input).trim();
  if (!value) return;
  try {
    const url = new URL(value.includes("://") ? value : `https://${value}`);
    return url.hostname || undefined;
  } catch {
    return;
  }
}